/* eslint-disable no-warning-comments */
import DataGrid, { DataGridConfig } from 'components/DataGrid';
import LoadingScreen from 'components/LoadingScreen';
import OSInfo from 'components/OSInfo';
import PageLayout from 'components/PageLayout';
import history from 'history.js';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { truncateText } from 'lib/helper';
import React, {
  Fragment,
  Suspense,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CardTitle, Row, Col } from 'reactstrap';

import Filters from './Filters';
import RowActions from './RowActions';
import {
  orderByAtom,
  sortOrderAtom,
  caListingDataAtom,
  checkedListAtom,
  // isCheckAllAtom,
  selectedCloudAgentsFilterAtom,
  activeCAColumnKeysAtom,
  paginationConfigAtom,
  DEFAULT_ACTIVE_CA_LISTING_COLUMNS,
  componentHeightAtom,
  DEFAULT_AGED_OUT_CA_LISTING_COLUMNS,
  agedOutCAColumnKeysAtom,
  forceRerenderUIAtom,
} from './state';
import AgingOutLastDate from '../../components/AgingOutLastDate/AgingOutLastDate';
import { getDrillDownUrl, getItemFromLocalStorage } from '../../helper';
import { caRefreshAtom, appliedCAIdsAtom } from '../state';

const BASE_URL: string = getItemFromLocalStorage('approle') || '';
const CA_INSTALLATION_URL = `/${BASE_URL}/qualys/cloud_agents/installation_instructions`;

type Props = {
  // TODO: investigate the parameter usage and if not required remove
  setModalComponentData: (ModalDialogData) => void;
};

const Listing: React.FC<Props> = () => {
  // PRESERVED STATE
  const listingData = useAtomValue(caListingDataAtom);
  const [orderBy, setOrderBy] = useAtom(orderByAtom);
  const [sortOrder, setSortOrder] = useAtom(sortOrderAtom);

  const appliedRowIds = useAtomValue(appliedCAIdsAtom);
  const [selectedRowIds, setSelectedRowIds] = useAtom(checkedListAtom);
  const setCaRefresh = useSetAtom(caRefreshAtom);

  const [activeCAColumnKeys, updateActiveCAColumnKeys] = useAtom(
    activeCAColumnKeysAtom,
  );

  const [agedOutCAColumnKeys, updateAgedOutCAColumnKeys] = useAtom(
    agedOutCAColumnKeysAtom,
  );
  const [paginationConfig, setPaginationConfig] = useAtom(paginationConfigAtom);

  const [loading, _setLoading] = useState(false);

  const targetRef = useRef<HTMLDivElement>(null);
  const setComponentHeight = useSetAtom(componentHeightAtom);
  const selectedCloudAgentsFilter = useAtomValue(selectedCloudAgentsFilterAtom);
  const forceRerenderUI = useAtomValue(forceRerenderUIAtom);

  const enableSelectionAcrossPages = true;

  useLayoutEffect(() => {
    if (targetRef.current) {
      setComponentHeight(targetRef.current.offsetHeight);
    }
  }, []);

  const isCloudAgentAgingOut = (rowData) => {
    // isOlderThen1(rowData["last_checked_in"]);
    const checkedInDate = new Date(rowData['last_checked_in']);
    const now = new Date();

    // Calculate the date 10 days ago from now
    const tenDaysAgo = new Date();
    tenDaysAgo.setDate(now.getDate() - 10);

    // // Calculate the date 30 days ago from now
    // const thirtyDaysAgo = new Date();
    // thirtyDaysAgo.setDate(now.getDate() - 30);
    // Check if the date is more than 10 days old but not more than 30 days old
    // return checkedInDate < tenDaysAgo && checkedInDate >= thirtyDaysAgo;
    return checkedInDate < tenDaysAgo;
  };

  const isDeletedFilterSelected = () => {
    return selectedCloudAgentsFilter === 'deleted';
  };

  const getColumnsDisplayConfig = () => {
    if (selectedCloudAgentsFilter === 'deleted') {
      return {
        enabledColumnKeys: agedOutCAColumnKeys,
        defaultColumnKeys: DEFAULT_AGED_OUT_CA_LISTING_COLUMNS,
        keysToBeExcluded: [
          'total_vuln',
          'new_vuln',
          'last_vuln_scan',
          'last_checked_in',
        ],
        onColumnsDisplayChange: (updatedColumnKeys) => {
          updateAgedOutCAColumnKeys(updatedColumnKeys);
        },
      };
    } else {
      return {
        enabledColumnKeys: activeCAColumnKeys,
        defaultColumnKeys: DEFAULT_ACTIVE_CA_LISTING_COLUMNS,
        keysToBeExcluded: [
          'deleted_cloudagent__open_vuln_counts',
          'deleted_cloudagent__closed_vuln_counts',
          'deleted_cloudagent__deleted_at',
          'deleted_cloudagent__deleted_by',
        ],
        onColumnsDisplayChange: (updatedColumnKeys) => {
          updateActiveCAColumnKeys(updatedColumnKeys);
        },
      };
    }
  };

  const getGridConfig = (): DataGridConfig => {
    const config: DataGridConfig = {
      rowIdKey: 'id',

      columns: [
        {
          key: 'id',
          label: 'Id',
          sortable: true,
          headerTooltip: 'Row identifier in SP360 database',
        },
        {
          key: 'ca_id',
          label: 'Cloud Agent Id',
          sortable: true,
          orderBy: 'ca_id',
          headerTooltip: 'Cloud Agent Id at Qualys',
        },
        {
          key: 'agentkey__title',
          label: 'Agent Title',
          sortable: true,
          orderBy: 'agentkey__title',
          headerTooltip: 'Cloud Agent activation key title at Qualys',
          overrideContents: (rowData) => (
            <Fragment>
              {rowData['agentkey__title']
                ? rowData['agentkey__title'].split('-')[1].trim()
                : ''}
            </Fragment>
          ),
          tooltip: (rowData) => (
            <Fragment>
              {rowData['agentkey__title']
                ? rowData['agentkey__title'].split('-')[1].trim()
                : ''}
            </Fragment>
          ),
          headerCellStyle: { width: 'auto' },
          rowCellStyle: { width: 'auto', textDecoration: 'none' },
        },
        {
          key: 'agentkey__activation_key',
          label: 'Agent Key',
          sortable: true,
          orderBy: 'agentkey__activation_key',
          headerTooltip: 'Cloud Agent Key at Qualys',
          tooltip: (rowData) => rowData['agentkey__activation_key'],
          headerCellStyle: { width: 'auto' },
          rowCellStyle: { width: 'auto', textDecoration: 'none' },
          enableCopyToClipboard: true,
        },
        {
          key: 'os',
          label: 'Operating System',
          overrideContents: (rowData) => (
            <OSInfo id={rowData['id']} os={rowData['os']} />
          ),
          tooltip: (rowData) => rowData['os'],
          sortable: true,
          orderBy: 'os',
          headerTooltip: 'Host operating system',
        },
        {
          key: 'name',
          label: 'Host Name',
          sortable: true,
          orderBy: 'name',
          headerCellStyle: { width: 'auto' },
          rowCellStyle: { width: 'auto', textDecoration: 'none' },
          headerTooltip: 'Host system name',
        },
        {
          key: 'name_with_type',
          label: 'Host',
          sortable: true,
          orderBy: 'name',
          headerCellStyle: { width: 'auto' },
          rowCellStyle: { width: 'auto', textDecoration: 'none' },
          overrideContents: (rowData) => (
            <Fragment>
              <i
                className={`fa fa-${
                  rowData['server_or_workstation'] === 'Workstations'
                    ? 'desktop'
                    : 'server'
                }`}
                style={{ color: '#0e74bc', fontSize: '18px' }}
              ></i>
              &nbsp;&nbsp;
              <strong>{truncateText(rowData.name, 30)}</strong>
            </Fragment>
          ),
          tooltip: (rowData) => (
            <Fragment>
              <strong>Host Type:</strong>&nbsp;&nbsp;
              <i
                className={`fa fa-${
                  rowData['server_or_workstation'] === 'Workstations'
                    ? 'desktop'
                    : 'server'
                }`}
                style={{ color: '#0e74bc', fontSize: '18px' }}
              ></i>{' '}
              {rowData['server_or_workstation'] === 'Workstations'
                ? 'Workstation'
                : rowData['server_or_workstation']}
              <br />
              <strong>Host Name: </strong>
              {rowData.name}
              {rowData.alias ? (
                <>
                  <br />
                  <strong>Alias: </strong>
                  {rowData.alias}
                </>
              ) : (
                ''
              )}
            </Fragment>
          ),
          headerTooltip: 'Host type and name',
        },
        {
          key: 'alias',
          label: 'Alias',
          sortable: true,
          headerTooltip: 'Custom alias',
        },
        {
          key: 'address',
          label: 'Address',
          sortable: true,
          orderBy: 'big_ip',
          headerTooltip: 'Host system IPv4 address',
        },
        {
          key: 'server_or_workstation',
          label: 'Type',
          sortable: true,
          orderBy: 'server_or_workstation',
          render: (rowData) =>
            rowData['server_or_workstation'] === 'Workstations'
              ? 'Workstation'
              : rowData['server_or_workstation'],
          headerTooltip: 'Indicates if it is a Server or Workstation',
        },
        // NON-DELETED COLUMNS
        {
          key: 'total_vuln',
          label: 'Vuln. Count',
          sortable: true,
          headerTooltip: 'Total number of vulnerabilities',
          overrideContents: (rowData) => (
            <label
              className="label btn slim"
              style={{ color: 'rgb(255, 255, 255)', cursor: 'pointer' }}
            >
              {rowData.total_vuln}
            </label>
          ),
          onClick: (rowData) =>
            history.push(
              getDrillDownUrl({
                type: 'open',
                sourceType:
                  rowData['server_or_workstation'] === 'Server'
                    ? 'ca_data'
                    : 'ca_data-workstations',
                hosts_in: [rowData.id],
                severity: '',
                issueType: '',
                qid: '',
                severity_in: undefined,
              }),
              // `/${BASE_URL}/qualys/vulnerabilities?source_type=${
              //   rowData['server_or_workstation'] === 'Server'
              //     ? 'ca_data'
              //     : 'ca_data-workstations'
              // }&cloudagent_id=${rowData.id}`,
            ),
        },
        {
          key: 'deleted_cloudagent__open_vuln_counts',
          label: 'Open Vuln. Count',
          sortable: true,
          headerTooltip: 'Number of open vulnerabilities',
          overrideContents: (rowData) => (
            <label
              className="label btn slim"
              style={{ color: 'rgb(255, 255, 255)', cursor: 'pointer' }}
            >
              {rowData.deleted_cloudagent__open_vuln_counts}
            </label>
          ),
        },
        {
          key: 'deleted_cloudagent__closed_vuln_counts',
          label: 'Closed Vuln. Count',
          sortable: true,
          headerTooltip: 'Number of closed vulnerabilities',
          overrideContents: (rowData) => (
            <label
              className="label btn slim"
              style={{ color: 'rgb(255, 255, 255)', cursor: 'pointer' }}
            >
              {rowData.deleted_cloudagent__closed_vuln_counts}
            </label>
          ),
        },
        {
          key: 'deleted_cloudagent__deleted_at',
          label: 'Deleted At',
          sortable: true,
          headerTooltip: 'Cloud Agent deletion date',
        },
        {
          key: 'deleted_cloudagent__deleted_by',
          label: 'Deleted By',
          sortable: true,
          headerTooltip: 'User who deleted the Cloud Agent',
          // TODO: user info tooltip
        },
        {
          key: 'new_vuln',
          label: 'New Vuln.',
          sortable: true,
          headerTooltip: 'Number of new vulnerabilities',
          overrideContents: (rowData) => (
            <label
              className="label btn slim"
              style={{ color: 'rgb(255, 255, 255)' }}
            >
              {rowData.new_vuln}
            </label>
          ),
        },
        {
          key: 'ca_created',
          label: 'Created At',
          sortable: true,
          orderBy: 'ca_created',
          headerTooltip: 'Installation date of the cloud agent',
        },
        {
          key: 'last_vuln_scan',
          label: 'Last Scanned At',
          sortable: true,
          orderBy: 'last_vuln_scan',
          headerTooltip: 'Last scanned date',
        },
        {
          key: 'last_checked_in',
          label: 'Last Checked In',
          sortable: true,
          orderBy: 'last_checked_in',
          headerTooltip: 'Last checked in date',
          render: (rowData) => (
            <AgingOutLastDate
              lastDate={rowData['last_checked_in']}
              totalDays={1 * 30}
              dangerDaysDiff={10}
              warningDaysDiff={20}
              tooltipTitle="Cloud agent is inactive and will age out"
            />
          ),
        },
        {
          key: 'actions',
          label: 'Actions',
          render: (rowData) => <RowActions rowData={rowData} />,
          headerCellStyle: {
            textAlign: 'right',
            maxWidth: 'auto',
            minWidth: '150px !important',
          },
          rowCellStyle: {
            textAlign: 'right',
            minWidth: '150px !important',
            maxWidth: 'auto',
          },
        },
      ],
      columnsDisplayConfig: getColumnsDisplayConfig(),
      rowConfig: {
        classRules: [
          // {
          //   className: "new-row",
          //   ruleFunction: (rowData) => isWithinLastMinute(rowData["created_at"]),
          // },
          {
            className: 'warning-row',
            ruleFunction: isCloudAgentAgingOut,
          },
          {
            className: 'danger-row',
            ruleFunction: isDeletedFilterSelected,
          },
          {
            className: 'applied-row',
            ruleFunction: (rowData) =>
              rowData['isAppliedForDashboard'] === true,
          },
        ],
      },
      paginationConfig: {
        total_count: listingData?.count || 0,
        pageNumber: paginationConfig.page - 1, // page number expects a zero based index
        pageSize: paginationConfig.page_size,
        onPageChange: (newPage: number) => {
          // console.log("page number changed to " + newPage);
          // page number returns a zero based index, so incrementing by 1
          setPaginationConfig({
            page_size: paginationConfig.page_size,
            page: newPage + 1,
          });
          if (!enableSelectionAcrossPages) {
            // reset selected row ids on  change
            setSelectedRowIds([]);
          }
        },
        onPageSizeChange: (newPageSize: number) => {
          // console.log("page size changed to " + newPageSize);
          // reset page number on page size change
          setPaginationConfig({ page_size: newPageSize, page: 1 });

          // reset selected row ids on  change
          setSelectedRowIds([]);
        },
      },
      sortingConfig: {
        orderBy: orderBy,
        order: sortOrder,
        onOrderByChange: (value: string) => setOrderBy(value),
        onOrderChange: (value) => setSortOrder(value),
      },

      //   TODO: add more configuration options for responsive columns display, for different screen sizes
    };

    // Conditionally add row selection configuration, should not be available for deleted CA's
    config.rowSelectionConfig = isDeletedFilterSelected()
      ? undefined
      : {
          enableSelectionAcrossPages: enableSelectionAcrossPages,
          preSelectedRows: selectedRowIds,
          onSelectionChange: (updatedRowIds) => {
            setSelectedRowIds(updatedRowIds);
          },
        };

    return config;
  };

  const config = useMemo<DataGridConfig>(() => {
    return getGridConfig();
  }, [
    listingData,
    activeCAColumnKeys,
    agedOutCAColumnKeys,
    setCaRefresh,
    forceRerenderUI,
    paginationConfig,
    selectedRowIds,
    appliedRowIds,
    selectedCloudAgentsFilter,
  ]);

  return (
    <div ref={targetRef}>
      <PageLayout
        id="sast_repos_listing"
        heading={
          <Row>
            <Col md="auto" sm="12">
              <CardTitle>
                <h4>
                  Cloud Agents
                  <Suspense fallback={<></>}>
                    <label className="label btn">
                      {listingData?.count || 0}
                    </label>
                  </Suspense>
                </h4>
                <a
                  href={CA_INSTALLATION_URL}
                  id="CloudAgentsInstruction"
                  target="_blank"
                  rel="noreferrer"
                  className="installation_instructions"
                >
                  Get Installation Instructions
                </a>
              </CardTitle>
            </Col>
          </Row>
        }
        description="List of Cloud Agents."
        filteringOptions={
          <Suspense fallback={<LoadingScreen />}>
            <Filters />
          </Suspense>
        }
      >
        <Suspense fallback={<LoadingScreen />}>
          <DataGrid
            config={config}
            data={listingData?.items}
            loading={loading}
          />
        </Suspense>
      </PageLayout>
    </div>
  );
};

const CAList = ({ setModalComponentData }: Props) => {
  const componentHeight = useAtomValue(componentHeightAtom);

  return (
    <Suspense fallback={<LoadingScreen height={componentHeight} />}>
      <Listing setModalComponentData={setModalComponentData} />
    </Suspense>
  );
};

export default CAList;
